import { Page, Text, View } from "@react-pdf/renderer";
import { Style } from "@react-pdf/types";
import { useCallback, useMemo } from "react";

import PdfDocumentHtml from "./components/pdf-document-html/pdf-document-html.component";
import { FORM_TEXT_MARK, formPageLayout } from "./form-print.utils";
import type { PdfDocumentPage } from "./pdf-document-page.type";
import { PdfPageChromeContext, formPageChrome } from "./pdf-page-chrome";

const viewStyle: Style = {
  position: "absolute",
  bottom: 30,
  left: 45,
  right: 45,
  textAlign: "center",
  color: "#616161",
  fontSize: 12
};

// The space kept clear above the footer, in a legacy page's px.
const NON_FIT_FOOTER_SPACE_PX = 175;
const FIT_FOOTER_SPACE_PX = 10;

const pageNumberStyle = { fontSize: 12 };

const emptyPageNumberRender = ({
  pageNumber,
  subPageNumber
}: {
  pageNumber: number;
  subPageNumber: number;
}) => {
  return <Text></Text>;
};

export default function PdfDocumentPageItem({
  dpi,
  header,
  footer,
  contentStyle,
  bodyStyle,
  bodyHtml,
  pdfNativeTablesEnabled,
  printableTableLayoutFixesEnabled,
  children,
  showPageNumber,
  pageNumberPrefix,
  pageNumberPostfix,
  formSettings,
  fitFooter
}: PdfDocumentPage & { fitFooter?: boolean }) {
  const renderPageNumber = useCallback(
    ({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) =>
      totalPages === 1
        ? null
        : `${pageNumberPrefix}Page ${pageNumber} of ${totalPages}${pageNumberPostfix}`,
    [pageNumberPostfix, pageNumberPrefix]
  );

  const formLayout = useMemo(
    () => (formSettings ? formPageLayout(formSettings) : null),
    [formSettings]
  );
  // ENG-2277: a Form's page draws the header and footer at the size they print on any other document,
  // fitted between its own margins (see pdf-page-chrome).
  const formChrome = useMemo(
    () => (formSettings ? formPageChrome(formSettings) : null),
    [formSettings]
  );

  // A Form's margins are its own print setting, so they win over a caller's page style.
  const pageStyle = useMemo(
    () =>
      formLayout
        ? { ...bodyStyle, ...formLayout.padding }
        : {
            paddingTop: 40,
            paddingBottom: 60,
            paddingHorizontal: 50,
            ...bodyStyle
          },
    [bodyStyle, formLayout]
  );
  const renderFooter = useMemo(() => {
    if (!footer) return null;
    const footerSpacePx = fitFooter
      ? FIT_FOOTER_SPACE_PX
      : NON_FIT_FOOTER_SPACE_PX;
    return (
      <>
        <View
          style={{
            height: `${footerSpacePx * (formChrome?.scale ?? 1)}px`,
            width: "100%"
          }}
          render={emptyPageNumberRender}
        />
        {footer}
      </>
    );
  }, [fitFooter, footer, formChrome]);

  const withChrome = (node: React.ReactNode) =>
    formChrome ? (
      <PdfPageChromeContext.Provider value={formChrome}>
        {node}
      </PdfPageChromeContext.Provider>
    ) : (
      node
    );

  return (
    <Page
      dpi={formLayout?.dpi ?? dpi}
      size={formLayout?.size ?? "A4"}
      style={pageStyle}
    >
      {Boolean(header) ? withChrome(header) : null}
      {Boolean(children) && children}
      {Boolean(bodyHtml) && (
        <View
          style={
            formLayout ? { ...contentStyle, ...FORM_TEXT_MARK } : contentStyle
          }
        >
          <PdfDocumentHtml
            bodyHtml={bodyHtml}
            pdfNativeTablesEnabled={pdfNativeTablesEnabled}
            printableTableLayoutFixesEnabled={printableTableLayoutFixesEnabled}
            formSettings={formSettings}
          />
        </View>
      )}
      {/* ENG-2277: a Form prints like its original, which carries its own page numbers if it has any. */}
      {showPageNumber && !formLayout && (
        <View fixed style={viewStyle}>
          <Text style={pageNumberStyle} render={renderPageNumber}></Text>
        </View>
      )}

      {withChrome(renderFooter)}
    </Page>
  );
}
